import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Search, Filter, ExternalLink, Zap, Network, Gauge,
  ShieldCheck, Building2, Car, Umbrella, Crown, Shield, Landmark,
  FileCode, BadgeCheck, ChevronDown, ChevronRight, Check, X, Globe,
  TrendingUp, BarChart3, Star, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LogoFull } from '@/components/Logo';
import { api } from '@/services/api';
import { toast } from 'sonner';

const ICON_MAP = {
  zap: Zap, network: Network, gauge: Gauge, 'shield-check': ShieldCheck,
  'building-2': Building2, car: Car, umbrella: Umbrella, crown: Crown,
  shield: Shield, landmark: Landmark, 'file-code': FileCode,
  'badge-check': BadgeCheck, building: Building2
};

const TYPE_CONFIG = {
  aggregator: { label: 'Aggregator', color: '#00AEEF', bg: '#00AEEF15' },
  carrier:    { label: 'Carrier',    color: '#1A3A64', bg: '#1A3A6415' },
  standard:   { label: 'Standard',   color: '#F59E0B', bg: '#F59E0B15' }
};

const CarrierCard = ({ carrier, onSelect, isSelected }) => {
  const Icon = ICON_MAP[carrier.logo_icon] || Shield;
  const typeCfg = TYPE_CONFIG[carrier.type] || TYPE_CONFIG.carrier;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={() => onSelect(carrier)}
      className={`bg-white rounded-xl border p-5 cursor-pointer transition-all hover:shadow-md ${isSelected ? 'border-[#00AEEF] shadow-md ring-1 ring-[#00AEEF]/20' : 'border-[#DCE4ED]'}`}
      data-testid={`carrier-card-${carrier.carrier_id}`}
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: typeCfg.bg }}>
          <Icon size={20} style={{ color: typeCfg.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-[#0D1B2A] truncate">{carrier.name}</h3>
            {carrier.is_configured && <Check size={14} className="text-[#83CF44] flex-shrink-0" />}
          </div>
          <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-medium mt-0.5" style={{ backgroundColor: typeCfg.bg, color: typeCfg.color }}>
            {typeCfg.label}
          </span>
        </div>
      </div>
      <p className="text-xs text-[#8DA0B9] line-clamp-2 mb-3">{carrier.description}</p>
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-[#8DA0B9]">{carrier.supported_products?.length || 0} products</span>
        {carrier.am_best_rating && (
          <span className="text-[10px] font-medium text-[#1A3A64] flex items-center gap-1">
            <Star size={10} className="text-[#F59E0B]" /> {carrier.am_best_rating}
          </span>
        )}
        <span className={`text-[10px] font-medium ${carrier.is_configured ? 'text-[#83CF44]' : 'text-[#8DA0B9]'}`}>
          {carrier.is_configured ? 'Connected' : 'Ready'}
        </span>
      </div>
    </motion.div>
  );
};

const CarrierMarketplace = () => {
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [quoteMode, setQuoteMode] = useState(false);
  const [quoteResult, setQuoteResult] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [multiResults, setMultiResults] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.getCarriers();
        setCarriers(data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = carriers.filter(c => {
    const matchSearch = !search || c.name.toLowerCase().includes(search.toLowerCase()) || c.description?.toLowerCase().includes(search.toLowerCase());
    const matchType = !typeFilter || c.type === typeFilter;
    return matchSearch && matchType;
  });

  const aggregators = filtered.filter(c => c.type === 'aggregator');
  const directCarriers = filtered.filter(c => c.type === 'carrier');
  const standards = filtered.filter(c => c.type === 'standard');

  const handleMultiQuote = async () => {
    setQuoting(true);
    try {
      const result = await api.requestMultiQuote({
        coverages: ['general_liability', 'cyber_liability', 'workers_comp'],
        business_info: { business_name: 'Demo Business', industry: 'technology', employee_count: 25, annual_revenue: 3000000, state: 'CA' }
      });
      setMultiResults(result);
      toast.success(`${result.carriers_quoted} carrier quotes received!`);
    } catch (e) { toast.error('Multi-quote failed'); }
    finally { setQuoting(false); }
  };

  const handleSingleQuote = async (carrierId) => {
    setQuoting(true);
    try {
      const result = await api.requestCarrierQuote(carrierId, {
        coverages: selected?.supported_products?.slice(0, 3) || ['general_liability'],
        business_info: { business_name: 'Demo Business', industry: 'technology', employee_count: 25, annual_revenue: 3000000 }
      });
      setQuoteResult(result);
      toast.success('Quote received!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Quote failed'); }
    finally { setQuoting(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#F0F4F8] flex items-center justify-center">
      <div className="animate-spin w-8 h-8 border-4 border-[#1A3A64] border-t-transparent rounded-full" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F0F4F8]" data-testid="carrier-marketplace-page">
      <header className="bg-white border-b border-[#DCE4ED] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/builder" className="flex items-center gap-2 text-[#8DA0B9] hover:text-[#0D1B2A] transition-colors" data-testid="back-to-builder">
              <ArrowLeft size={18} /><span className="text-sm">Builder</span>
            </Link>
            <div className="w-px h-6 bg-[#DCE4ED]" />
            <h1 className="text-lg font-semibold text-[#0D1B2A]" style={{ fontFamily: 'Outfit, sans-serif' }}>Carrier Marketplace</h1>
          </div>
          <div className="flex items-center gap-3">
            <Button size="sm" onClick={handleMultiQuote} disabled={quoting} className="bg-[#00AEEF] hover:bg-[#0098D4] text-white" data-testid="multi-quote-btn">
              {quoting ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Zap size={14} className="mr-1" />}
              Multi-Carrier Quote
            </Button>
            <LogoFull size="small" />
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Bar */}
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-[#DCE4ED] p-4">
            <p className="text-xs text-[#8DA0B9]">Aggregators</p>
            <p className="text-2xl font-bold text-[#00AEEF]">{carriers.filter(c => c.type === 'aggregator').length}</p>
          </div>
          <div className="bg-white rounded-xl border border-[#DCE4ED] p-4">
            <p className="text-xs text-[#8DA0B9]">Direct Carriers</p>
            <p className="text-2xl font-bold text-[#1A3A64]">{carriers.filter(c => c.type === 'carrier').length}</p>
          </div>
          <div className="bg-white rounded-xl border border-[#DCE4ED] p-4">
            <p className="text-xs text-[#8DA0B9]">Standards</p>
            <p className="text-2xl font-bold text-[#F59E0B]">{carriers.filter(c => c.type === 'standard').length}</p>
          </div>
          <div className="bg-white rounded-xl border border-[#DCE4ED] p-4 hidden sm:block">
            <p className="text-xs text-[#8DA0B9]">Connected</p>
            <p className="text-2xl font-bold text-[#83CF44]">{carriers.filter(c => c.is_configured).length}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-6">
          <div className="relative flex-1 max-w-sm">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8DA0B9]" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search carriers..." className="pl-9 h-9" data-testid="carrier-search" />
          </div>
          <div className="relative">
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="border border-[#DCE4ED] rounded-lg px-3 py-2 text-sm appearance-none bg-white pr-8" data-testid="carrier-type-filter">
              <option value="">All Types</option>
              <option value="aggregator">Aggregators</option>
              <option value="carrier">Direct Carriers</option>
              <option value="standard">Standards</option>
            </select>
            <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#8DA0B9] pointer-events-none" />
          </div>
        </div>

        {/* Multi-Quote Results */}
        <AnimatePresence>
          {multiResults && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mb-6">
              <div className="bg-white rounded-xl border border-[#00AEEF] p-6" data-testid="multi-quote-results">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-base font-semibold text-[#0D1B2A]">Multi-Carrier Comparison ({multiResults.carriers_quoted} carriers)</h2>
                  <Button variant="ghost" size="sm" onClick={() => setMultiResults(null)}><X size={14} /></Button>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {multiResults.results?.map((r, i) => {
                    const Icon = ICON_MAP[r.logo_icon] || Shield;
                    return (
                      <div key={r.carrier_id} className={`p-4 rounded-lg border ${i === 0 ? 'border-[#83CF44] bg-[#83CF4408]' : 'border-[#DCE4ED]'}`} data-testid={`multi-result-${r.carrier_id}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <Icon size={16} className="text-[#3D5A80]" />
                          <span className="text-sm font-medium text-[#0D1B2A]">{r.carrier_name}</span>
                          {i === 0 && <span className="text-[10px] px-1.5 py-0.5 bg-[#83CF44] text-white rounded-full">Best</span>}
                        </div>
                        <p className="text-xl font-bold text-[#1A3A64]">${r.total_monthly_premium}/mo</p>
                        <p className="text-xs text-[#8DA0B9]">${r.total_annual_premium}/yr &bull; {r.coverages_quoted} coverages</p>
                        {r.am_best_rating && <p className="text-[10px] text-[#F59E0B] mt-1">AM Best: {r.am_best_rating}</p>}
                        {r.is_simulated && <p className="text-[10px] text-[#8DA0B9] italic mt-1">Simulated</p>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Carrier Grid */}
          <div className="lg:col-span-3 space-y-6">
            {aggregators.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-[#8DA0B9] uppercase tracking-wider mb-3">Aggregators — Multi-Carrier Access</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {aggregators.map(c => <CarrierCard key={c.carrier_id} carrier={c} onSelect={setSelected} isSelected={selected?.carrier_id === c.carrier_id} />)}
                </div>
              </div>
            )}
            {directCarriers.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-[#8DA0B9] uppercase tracking-wider mb-3">Direct Carriers</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {directCarriers.map(c => <CarrierCard key={c.carrier_id} carrier={c} onSelect={setSelected} isSelected={selected?.carrier_id === c.carrier_id} />)}
                </div>
              </div>
            )}
            {standards.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-[#8DA0B9] uppercase tracking-wider mb-3">Standards & Infrastructure</h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {standards.map(c => <CarrierCard key={c.carrier_id} carrier={c} onSelect={setSelected} isSelected={selected?.carrier_id === c.carrier_id} />)}
                </div>
              </div>
            )}
          </div>

          {/* Detail Panel */}
          <div className="lg:col-span-2">
            <div className="sticky top-24">
              {selected ? (
                <motion.div key={selected.carrier_id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className="bg-white rounded-xl border border-[#DCE4ED] overflow-hidden" data-testid="carrier-detail-panel">
                  <div className="p-5 border-b border-[#DCE4ED]">
                    <div className="flex items-center gap-3 mb-3">
                      {(() => { const Icon = ICON_MAP[selected.logo_icon] || Shield; return <Icon size={24} className="text-[#1A3A64]" />; })()}
                      <div>
                        <h2 className="text-base font-semibold text-[#0D1B2A]">{selected.name}</h2>
                        <p className="text-xs text-[#8DA0B9]">{selected.category}</p>
                      </div>
                    </div>
                    <p className="text-sm text-[#3D5A80]">{selected.description}</p>
                  </div>

                  <div className="p-5 space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-2 bg-[#F0F4F8] rounded-lg">
                        <p className="text-[10px] text-[#8DA0B9]">Integration</p>
                        <p className="text-xs text-[#0D1B2A] font-medium">{selected.integration_type}</p>
                      </div>
                      <div className="p-2 bg-[#F0F4F8] rounded-lg">
                        <p className="text-[10px] text-[#8DA0B9]">Data Format</p>
                        <p className="text-xs text-[#0D1B2A] font-medium">{selected.data_format || 'N/A'}</p>
                      </div>
                      {selected.am_best_rating && (
                        <div className="p-2 bg-[#F0F4F8] rounded-lg">
                          <p className="text-[10px] text-[#8DA0B9]">AM Best</p>
                          <p className="text-xs text-[#0D1B2A] font-medium">{selected.am_best_rating}</p>
                        </div>
                      )}
                      <div className="p-2 bg-[#F0F4F8] rounded-lg">
                        <p className="text-[10px] text-[#8DA0B9]">Response Time</p>
                        <p className="text-xs text-[#0D1B2A] font-medium">{selected.avg_response_time || 'N/A'}</p>
                      </div>
                    </div>

                    {selected.features?.length > 0 && (
                      <div>
                        <p className="text-xs text-[#8DA0B9] mb-2">Features</p>
                        <div className="flex flex-wrap gap-1.5">
                          {selected.features.map((f, i) => (
                            <span key={i} className="px-2 py-0.5 bg-[#F0F4F8] rounded-full text-[10px] text-[#3D5A80]">{f}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {selected.supported_products?.length > 0 && (
                      <div>
                        <p className="text-xs text-[#8DA0B9] mb-2">Supported Products ({selected.supported_products.length})</p>
                        <div className="flex flex-wrap gap-1.5">
                          {selected.supported_products.map(p => (
                            <span key={p} className="px-2 py-0.5 bg-[#1A3A6410] rounded-full text-[10px] text-[#1A3A64] capitalize">{p.replace(/_/g, ' ')}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {selected.carriers_connected?.length > 0 && (
                      <div>
                        <p className="text-xs text-[#8DA0B9] mb-1">Connected Carriers</p>
                        <p className="text-xs text-[#3D5A80]">{Array.isArray(selected.carriers_connected) ? selected.carriers_connected.join(', ') : selected.carriers_connected}</p>
                      </div>
                    )}

                    {selected.standards_supported?.length > 0 && (
                      <div>
                        <p className="text-xs text-[#8DA0B9] mb-1">Standards</p>
                        <p className="text-xs text-[#3D5A80]">{selected.standards_supported.join(', ')}</p>
                      </div>
                    )}

                    <div className="pt-3 border-t border-[#DCE4ED] flex flex-col gap-2">
                      <div className={`p-2 rounded-lg text-xs text-center font-medium ${selected.is_configured ? 'bg-[#83CF4415] text-[#83CF44]' : 'bg-[#F59E0B15] text-[#F59E0B]'}`}>
                        {selected.is_configured ? 'API Connected' : 'API Keys Required — Simulation Mode'}
                      </div>
                      {selected.type !== 'standard' && (
                        <Button size="sm" onClick={() => handleSingleQuote(selected.carrier_id)} disabled={quoting} className="bg-[#1A3A64] hover:bg-[#0D1B2A] text-white w-full" data-testid="carrier-quote-btn">
                          {quoting ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Zap size={14} className="mr-1" />}
                          Get Quote
                        </Button>
                      )}
                      {selected.website && (
                        <a href={selected.website} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-1 text-xs text-[#00AEEF] hover:underline">
                          <Globe size={12} /> Visit Website <ExternalLink size={10} />
                        </a>
                      )}
                    </div>

                    {/* Single Quote Result */}
                    {quoteResult && quoteResult.carrier_id === selected.carrier_id && quoteResult.response && (
                      <div className="p-4 bg-[#F0F4F8] rounded-lg border border-[#DCE4ED]" data-testid="single-quote-result">
                        <p className="text-xs text-[#8DA0B9] mb-2">Quote Result</p>
                        <p className="text-xl font-bold text-[#1A3A64]">${quoteResult.response.total_monthly_premium}/mo</p>
                        <p className="text-xs text-[#8DA0B9]">${quoteResult.response.total_annual_premium}/yr (10% annual discount)</p>
                        <div className="mt-2 space-y-1">
                          {quoteResult.response.line_items?.map(li => (
                            <div key={li.coverage_type} className="flex justify-between text-xs">
                              <span className="text-[#3D5A80]">{li.name}</span>
                              <span className="text-[#0D1B2A] font-medium">${li.monthly_premium}/mo</span>
                            </div>
                          ))}
                        </div>
                        {quoteResult.is_simulated && <p className="text-[10px] text-[#F59E0B] italic mt-2">Simulated — connect API for real rates</p>}
                      </div>
                    )}
                  </div>
                </motion.div>
              ) : (
                <div className="bg-white rounded-xl border border-[#DCE4ED] p-12 text-center">
                  <Building2 size={32} className="mx-auto text-[#DCE4ED] mb-3" />
                  <p className="text-sm text-[#8DA0B9]">Select a carrier to view details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarrierMarketplace;
